import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  Ship,
  Package,
  Plane,
  Truck,
  ChevronRight,
  Send,
  CheckCircle,
  Clock,
  MapPin,
  Calendar,
  Weight,
  Box,
  Loader2,
  AlertCircle,
  FileText,
  Eye,
  X,
  DollarSign,
  Plus,
  Trash2,
} from "lucide-react";


const SERVICE_TYPES = [
  { id: "fcl", name: "FCL", desc: "Full Container Load", icon: Ship, color: "blue" },
  { id: "lcl", name: "LCL", desc: "Less Container Load", icon: Package, color: "emerald" },
  { id: "air", name: "Air", desc: "Air Freight", icon: Plane, color: "violet" },
  { id: "inland", name: "Inland", desc: "Road/Rail", icon: Truck, color: "amber" },
];

const CONTAINER_TYPES = [
  { value: "20GP", label: "20' Standard" },
  { value: "40GP", label: "40' Standard" },
  { value: "40HC", label: "40' High Cube" },
  { value: "45HC", label: "45' High Cube" },
  { value: "20RF", label: "20' Reefer" },
  { value: "40RF", label: "40' Reefer" },
  { value: "20FR", label: "20' Flat Rack (oversize)" },
  { value: "40FR", label: "40' Flat Rack (oversize)" },
  { value: "20OT", label: "20' Open Top (oversize)" },
  { value: "40OT", label: "40' Open Top (oversize)" },
  { value: "BREAKBULK", label: "Breakbulk / project cargo" },
];

const OVERSIZE_CONTAINERS = new Set(["20FR", "40FR", "20OT", "40OT", "BREAKBULK"]);

export default function CustomerQuotesPage() {
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState("request"); // "request" | "history"
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  // QR-ref captured from URL when WhatsApp bot links the customer here.
  // Backend uses it to MERGE this submission into the existing RFQ
  // instead of creating a duplicate.
  const [waQuoteRef, setWaQuoteRef] = useState("");
  
  // Form state
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    service_type: "",
    origin_city: "",
    origin_country: "",
    destination_city: "",
    destination_country: "",
    cargo_ready_date: "",
    commodity: "",
    cargo_details: [{
      description: "",
      quantity: 1,
      weight_kg: "",
      volume_cbm: "",
    }],
    container_type: "40HC",
    container_count: 1,
    // Maersk-style additions
    origin_service_mode: "CY",      // "CY" yard | "SD" door
    destination_service_mode: "CY",
    shipment_type: "standard",      // "standard" | "out_of_gauge" | "break_bulk" | "lcl_oversize"
    is_dangerous: false,
    shippers_own_container: false,
    weight_per_container_kg: "",
    service_contract_number: "",
    cc_emails: "",                  // comma-separated in UI, split on submit
    company_country: user?.company_country || "",
    // Oversize / out-of-gauge details (only sent when container_type ∈ flat-rack / open-top / breakbulk)
    is_oversize: false,
    oversize_length_cm: "",
    oversize_width_cm: "",
    oversize_height_cm: "",
    oversize_weight_kg: "",
    oversize_notes: "",
    // IDs of pre-uploaded cargo photos / technical drawings
    document_ids: [],
    uploaded_docs: [],   // [{ document_id, filename, document_type, size }]
    special_requirements: "",
    contact_name: user?.full_name || "",
    contact_email: user?.email || "",
    contact_phone: user?.phone || "",
  });

  // Hydrate from URL query params (WhatsApp prefill flow).
  // Bot sends users to /customer-quotes?wa=...&origin=...&destination=...&service=fcl
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const origin = params.get("origin");
    const destination = params.get("destination");
    const service = (params.get("service") || "").toLowerCase();
    const incoterm = (params.get("incoterm") || "").toUpperCase();
    const commodity = params.get("commodity");
    const weight = params.get("weight");
    const volume = params.get("volume");
    const pieces = params.get("pieces");
    const containerType = params.get("container_type");
    const containerCount = params.get("container_count");
    const dimensions = params.get("dimensions");
    const wa = params.get("wa");
    const qref = params.get("qref");
    const patch = {};
    if (qref) setWaQuoteRef(qref);
    if (origin) patch.origin_city = origin;
    if (destination) patch.destination_city = destination;
    if (service && ["fcl", "lcl", "air", "inland", "multimodal"].includes(service)) {
      patch.service_type = service;
    }
    if (commodity) patch.commodity = commodity;
    if (containerType) patch.container_type = containerType;
    if (containerCount) patch.container_count = parseInt(containerCount, 10) || 1;
    if (incoterm) patch.special_requirements = `Incoterm: ${incoterm}` +
      (dimensions ? ` · Dims: ${dimensions}cm` : "");
    if (pieces || weight || volume) {
      patch.cargo_details = [{
        description: commodity || dimensions || "Cargo",
        quantity: parseInt(pieces || "1", 10) || 1,
        weight_kg: weight || "",
        volume_cbm: volume || "",
      }];
    }
    if (wa && !patch.contact_phone) patch.contact_phone = wa.replace("whatsapp:", "");
    if (Object.keys(patch).length) {
      setFormData((prev) => ({ ...prev, ...patch }));
      toast.success(
        qref
          ? `Pre-filled from your WhatsApp request ${qref} ✨ Anything you add here will be merged into the same RFQ.`
          : "Pre-filled from your WhatsApp message ✨"
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === "history") {
      fetchQuotes();
    }
  }, [activeTab, token]);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/customer-portal/my-quotes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuotes(response.data.quotes || []);
    } catch (error) {
      // Fallback: Try to get from general quotes endpoint
      try {
        const response = await axios.get(`${API_URL}/quotes/my-quotes`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setQuotes(response.data.quotes || []);
      } catch (e) {
        console.error("Failed to fetch quotes");
        setQuotes([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateCargoItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      cargo_details: prev.cargo_details.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const addCargoItem = () => {
    setFormData(prev => ({
      ...prev,
      cargo_details: [...prev.cargo_details, {
        description: "",
        quantity: 1,
        weight_kg: "",
        volume_cbm: "",
      }]
    }));
  };

  const removeCargoItem = (index) => {
    if (formData.cargo_details.length > 1) {
      setFormData(prev => ({
        ...prev,
        cargo_details: prev.cargo_details.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.service_type) {
      toast.error("Please select a service type");
      setStep(1);
      return;
    }
    if (!formData.origin_city || !formData.destination_city) {
      toast.error("Please enter origin and destination");
      setStep(2);
      return;
    }
    
    setSubmitting(true);
    try {
      // Map frontend form fields to the backend Pydantic schema
      // (QuoteRequestCreate). Without this, fields like `cargo_details`
      // / `container_count` / `contact_email` are silently dropped.
      const cargoItems = (formData.cargo_details || []).map((c) => ({
        description: c.description || "",
        quantity: parseInt(c.quantity, 10) || 1,
        weight_kg: c.weight_kg ? parseFloat(c.weight_kg) : null,
        volume_cbm: c.volume_cbm ? parseFloat(c.volume_cbm) : null,
      })).filter((c) => c.description || c.weight_kg || c.volume_cbm);
      const totalWeight = cargoItems.reduce((s, c) => s + (c.weight_kg || 0), 0) || null;
      const totalVolume = cargoItems.reduce((s, c) => s + (c.volume_cbm || 0), 0) || null;

      await axios.post(`${API_URL}/quotes/request`, {
        service_type: formData.service_type,
        origin_country: formData.origin_country || "",
        origin_city: formData.origin_city || "",
        destination_country: formData.destination_country || "",
        destination_city: formData.destination_city || "",
        cargo_ready_date: formData.cargo_ready_date || null,
        commodity: formData.commodity || null,
        cargo_items: cargoItems,
        total_weight_kg: totalWeight,
        total_volume_cbm: totalVolume,
        container_type: formData.container_type || null,
        container_quantity: parseInt(formData.container_count, 10) || 1,
        is_oversize: !!formData.is_oversize || OVERSIZE_CONTAINERS.has(formData.container_type),
        oversize_length_cm: formData.oversize_length_cm ? parseFloat(formData.oversize_length_cm) : null,
        oversize_width_cm: formData.oversize_width_cm ? parseFloat(formData.oversize_width_cm) : null,
        oversize_height_cm: formData.oversize_height_cm ? parseFloat(formData.oversize_height_cm) : null,
        oversize_weight_kg: formData.oversize_weight_kg ? parseFloat(formData.oversize_weight_kg) : null,
        oversize_notes: formData.oversize_notes || null,
        document_ids: formData.document_ids || [],
        special_requirements: formData.special_requirements || null,
        company_name: user?.company_name || "",
        contact_name: formData.contact_name || user?.full_name || "",
        email: formData.contact_email || user?.email || "",
        phone: formData.contact_phone || user?.phone || null,
        // Pass the WA reference so the backend merges into the existing
        // RFQ instead of creating a duplicate.
        wa_quote_ref: waQuoteRef || undefined,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(
        waQuoteRef
          ? `Quote ${waQuoteRef} updated with your new details — sales has been re-notified.`
          : "Quote request submitted! We'll respond within 24 hours."
      );
      setFormData({
        service_type: "",
        origin_city: "",
        origin_country: "",
        destination_city: "",
        destination_country: "",
        cargo_ready_date: "",
        commodity: "",
        cargo_details: [{ description: "", quantity: 1, weight_kg: "", volume_cbm: "" }],
        container_type: "40HC",
        container_count: 1,
        origin_service_mode: "CY",
        destination_service_mode: "CY",
        shipment_type: "standard",
        is_dangerous: false,
        shippers_own_container: false,
        weight_per_container_kg: "",
        service_contract_number: "",
        cc_emails: "",
        company_country: user?.company_country || "",
        is_oversize: false,
        oversize_length_cm: "",
        oversize_width_cm: "",
        oversize_height_cm: "",
        oversize_weight_kg: "",
        oversize_notes: "",
        document_ids: [],
        uploaded_docs: [],
        special_requirements: "",
        contact_name: user?.full_name || "",
        contact_email: user?.email || "",
        contact_phone: user?.phone || "",
      });
      setStep(1);
      setActiveTab("history");
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit quote request");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-700",
      processing: "bg-blue-100 text-blue-700",
      quoted: "bg-green-100 text-green-700",
      accepted: "bg-emerald-100 text-emerald-700",
      declined: "bg-red-100 text-red-700",
      expired: "bg-gray-100 text-gray-500",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  // Upload one or more cargo photos / technical drawings BEFORE the quote
  // is submitted. The returned document_id is added to formData.document_ids
  // and auto-linked to the new quote on submit.
  const handleDocsSelected = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("document_type", /\.(jpe?g|png|gif|webp|heic)$/i.test(f.name) ? "cargo_photo" : "technical_drawing");
      fd.append("original_filename", f.name);
      try {
        const { data } = await axios.post(`${API_URL}/quote-documents/upload`, fd, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
        });
        setFormData((prev) => ({
          ...prev,
          document_ids: [...(prev.document_ids || []), data.document_id],
          uploaded_docs: [...(prev.uploaded_docs || []), data],
        }));
        toast.success(`Uploaded ${data.filename}`);
      } catch (err) {
        toast.error(`Failed to upload ${f.name}: ${err.response?.data?.detail || err.message}`);
      }
    }
  };

  const removeUploadedDoc = (document_id) => {
    setFormData((prev) => ({
      ...prev,
      document_ids: (prev.document_ids || []).filter((id) => id !== document_id),
      uploaded_docs: (prev.uploaded_docs || []).filter((d) => d.document_id !== document_id),
    }));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900">Get a Quote</h1>
          <p className="text-slate-600 mt-1">Request shipping quotes for your cargo</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b">
          <button
            onClick={() => setActiveTab("request")}
            className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition-colors ${
              activeTab === "request"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="tab-request"
          >
            <Plus className="w-4 h-4" />
            New Quote Request
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`flex items-center gap-2 px-4 py-3 font-medium border-b-2 transition-colors ${
              activeTab === "history"
                ? "border-blue-600 text-blue-600"
                : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
            data-testid="tab-history"
          >
            <FileText className="w-4 h-4" />
            My Quotes
          </button>
        </div>

        {/* Quote Request Form */}
        {activeTab === "request" && (
          <div className="bg-white rounded-xl border shadow-sm p-6">
            {/* Progress Steps */}
            <div className="flex items-center justify-between mb-8">
              {[
                { num: 1, label: "Service" },
                { num: 2, label: "Route" },
                { num: 3, label: "Cargo" },
                { num: 4, label: "Submit" },
              ].map((s, idx) => (
                <div key={s.num} className="flex items-center">
                  <button
                    onClick={() => setStep(s.num)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm ${
                      step >= s.num
                        ? "bg-blue-600 text-white"
                        : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    {step > s.num ? <CheckCircle className="w-5 h-5" /> : s.num}
                  </button>
                  <span className={`ml-2 text-sm ${step >= s.num ? "text-slate-700" : "text-slate-400"}`}>
                    {s.label}
                  </span>
                  {idx < 3 && (
                    <div className={`w-12 h-0.5 mx-4 ${step > s.num ? "bg-blue-600" : "bg-slate-200"}`} />
                  )}
                </div>
              ))}
            </div>

            {/* Step 1: Service Type */}
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <h2 className="text-xl font-semibold mb-4">Select Service Type</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {SERVICE_TYPES.map((type) => (
                    <button
                      key={type.id}
                      onClick={() => {
                        updateField("service_type", type.id);
                        setStep(2);
                      }}
                      className={`p-4 rounded-xl border-2 text-center transition-all ${
                        formData.service_type === type.id
                          ? `border-${type.color}-500 bg-${type.color}-50`
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                      data-testid={`service-${type.id}`}
                    >
                      <type.icon className={`w-8 h-8 mx-auto mb-2 ${
                        formData.service_type === type.id ? `text-${type.color}-600` : "text-slate-400"
                      }`} />
                      <p className="font-semibold">{type.name}</p>
                      <p className="text-xs text-slate-500">{type.desc}</p>
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 2: Route */}
            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-semibold mb-4">Shipping Route</h2>
                
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Origin */}
                  <div className="space-y-4">
                    <Label className="text-base font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-green-600" />
                      Origin
                    </Label>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm">City</Label>
                        <Input
                          value={formData.origin_city}
                          onChange={(e) => updateField("origin_city", e.target.value)}
                          placeholder="e.g., Shanghai"
                          data-testid="origin-city"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Country</Label>
                        <Input
                          value={formData.origin_country}
                          onChange={(e) => updateField("origin_country", e.target.value)}
                          placeholder="e.g., China"
                          data-testid="origin-country"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Destination */}
                  <div className="space-y-4">
                    <Label className="text-base font-medium flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-red-600" />
                      Destination
                    </Label>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm">City</Label>
                        <Input
                          value={formData.destination_city}
                          onChange={(e) => updateField("destination_city", e.target.value)}
                          placeholder="e.g., Ljubljana"
                          data-testid="destination-city"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Country</Label>
                        <Input
                          value={formData.destination_country}
                          onChange={(e) => updateField("destination_country", e.target.value)}
                          placeholder="e.g., Slovenia"
                          data-testid="destination-country"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Cargo Ready Date
                  </Label>
                  <Input
                    type="date"
                    value={formData.cargo_ready_date}
                    onChange={(e) => updateField("cargo_ready_date", e.target.value)}
                    className="max-w-xs"
                    data-testid="cargo-ready-date"
                  />
                </div>

                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    Back
                  </Button>
                  <Button onClick={() => setStep(3)}>
                    Continue
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Cargo Details */}
            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-semibold mb-4">Cargo Details</h2>

                <div>
                  <Label>Commodity / Product Type</Label>
                  <Input
                    value={formData.commodity}
                    onChange={(e) => updateField("commodity", e.target.value)}
                    placeholder="e.g., Electronic components, Furniture, Textiles..."
                    data-testid="commodity"
                  />
                </div>

                {/* FCL specific */}
                {formData.service_type === "fcl" && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Origin service mode</Label>
                        <select
                          value={formData.origin_service_mode}
                          onChange={(e) => updateField("origin_service_mode", e.target.value)}
                          className="w-full h-10 px-3 rounded-md border border-input bg-background"
                          data-testid="origin-service-mode"
                        >
                          <option value="CY">Container Yard (CY)</option>
                          <option value="SD">Store Door (SD)</option>
                        </select>
                      </div>
                      <div>
                        <Label>Destination service mode</Label>
                        <select
                          value={formData.destination_service_mode}
                          onChange={(e) => updateField("destination_service_mode", e.target.value)}
                          className="w-full h-10 px-3 rounded-md border border-input bg-background"
                          data-testid="destination-service-mode"
                        >
                          <option value="CY">Container Yard (CY)</option>
                          <option value="SD">Store Door (SD)</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Shipment type</Label>
                        <select
                          value={formData.shipment_type}
                          onChange={(e) => {
                            const v = e.target.value;
                            updateField("shipment_type", v);
                            if (v === "out_of_gauge" || v === "break_bulk") updateField("is_oversize", true);
                          }}
                          className="w-full h-10 px-3 rounded-md border border-input bg-background"
                          data-testid="shipment-type"
                        >
                          <option value="standard">Standard</option>
                          <option value="out_of_gauge">Out of Gauge</option>
                          <option value="break_bulk">Break Bulk</option>
                        </select>
                      </div>
                      <div>
                        <Label>Container Type</Label>
                        <select
                          value={formData.container_type}
                          onChange={(e) => {
                            const v = e.target.value;
                            updateField("container_type", v);
                            if (OVERSIZE_CONTAINERS.has(v)) updateField("is_oversize", true);
                          }}
                          className="w-full h-10 px-3 rounded-md border border-input bg-background"
                          data-testid="container-type"
                        >
                          {CONTAINER_TYPES.map((ct) => (
                            <option key={ct.value} value={ct.value}>{ct.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Number of Containers</Label>
                        <Input
                          type="number"
                          min="1"
                          value={formData.container_count}
                          onChange={(e) => updateField("container_count", parseInt(e.target.value) || 1)}
                          data-testid="container-count"
                        />
                      </div>
                      <div>
                        <Label>Weight per container (kg)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={formData.weight_per_container_kg}
                          onChange={(e) => updateField("weight_per_container_kg", e.target.value)}
                          placeholder="e.g., 22000"
                          data-testid="weight-per-container"
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                      <label className="flex items-center gap-2">
                        <input type="checkbox"
                          checked={!!formData.is_dangerous}
                          onChange={(e) => updateField("is_dangerous", e.target.checked)}
                          data-testid="is-dangerous" />
                        Considered dangerous (IMO/DGR — MSDS required)
                      </label>
                      <label className="flex items-center gap-2">
                        <input type="checkbox"
                          checked={!!formData.shippers_own_container}
                          onChange={(e) => updateField("shippers_own_container", e.target.checked)}
                          data-testid="soc" />
                        Shipper's own container (SOC)
                      </label>
                    </div>
                  </>
                )}

                {/* Oversize / out-of-gauge cargo block — shown automatically
                    for flat-rack / open-top / breakbulk OR when the customer
                    toggles "my cargo is oversize". */}
                {formData.service_type === "fcl" && (
                  <div className="border-2 border-dashed border-amber-300 bg-amber-50/40 rounded-lg p-4 space-y-3" data-testid="oversize-section">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <input
                        type="checkbox"
                        checked={!!formData.is_oversize || OVERSIZE_CONTAINERS.has(formData.container_type)}
                        onChange={(e) => updateField("is_oversize", e.target.checked)}
                        disabled={OVERSIZE_CONTAINERS.has(formData.container_type)}
                        data-testid="is-oversize"
                      />
                      <span>My cargo is <strong>oversize / out-of-gauge</strong> (flat-rack, open-top, breakbulk)</span>
                    </label>
                    {(!!formData.is_oversize || OVERSIZE_CONTAINERS.has(formData.container_type)) && (
                      <>
                        <p className="text-xs text-amber-800">
                          Please share the cargo dimensions and weight, and attach a photo or technical drawing — this lets us prepare an accurate flat-rack / open-top offer without back-and-forth.
                        </p>
                        <div className="grid grid-cols-4 gap-2">
                          <div>
                            <Label className="text-xs">Length (cm)</Label>
                            <Input type="number" min="0" value={formData.oversize_length_cm}
                              onChange={(e) => updateField("oversize_length_cm", e.target.value)}
                              data-testid="oversize-length" />
                          </div>
                          <div>
                            <Label className="text-xs">Width (cm)</Label>
                            <Input type="number" min="0" value={formData.oversize_width_cm}
                              onChange={(e) => updateField("oversize_width_cm", e.target.value)}
                              data-testid="oversize-width" />
                          </div>
                          <div>
                            <Label className="text-xs">Height (cm)</Label>
                            <Input type="number" min="0" value={formData.oversize_height_cm}
                              onChange={(e) => updateField("oversize_height_cm", e.target.value)}
                              data-testid="oversize-height" />
                          </div>
                          <div>
                            <Label className="text-xs">Weight (kg)</Label>
                            <Input type="number" min="0" value={formData.oversize_weight_kg}
                              onChange={(e) => updateField("oversize_weight_kg", e.target.value)}
                              data-testid="oversize-weight" />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs">Notes (lashing, stacking, lifting points…)</Label>
                          <Textarea
                            rows={2}
                            value={formData.oversize_notes}
                            onChange={(e) => updateField("oversize_notes", e.target.value)}
                            placeholder="E.g., 2 lifting eyes on top, requires forklift loading, fragile coating, 4 lashing rings."
                            data-testid="oversize-notes"
                          />
                        </div>
                      </>
                    )}

                    {/* Photos / technical drawings — always available, not only for oversize */}
                    <div>
                      <Label className="text-xs flex items-center gap-2">
                        <FileText className="w-3.5 h-3.5" />
                        Cargo photos / technical drawings (PDF, JPG, PNG, DWG, DXF, STEP — max 25 MB each)
                      </Label>
                      <input
                        type="file"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.dwg,.dxf,.step,.stp,.igs,.iges,.doc,.docx,.xls,.xlsx"
                        onChange={(e) => handleDocsSelected(e.target.files)}
                        className="block w-full text-sm mt-1"
                        data-testid="quote-doc-upload"
                      />
                      {(formData.uploaded_docs || []).length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {(formData.uploaded_docs || []).map((d) => (
                            <li key={d.document_id}
                                className="flex items-center justify-between text-xs bg-white border rounded px-2 py-1"
                                data-testid={`uploaded-doc-${d.document_id}`}>
                              <span className="truncate">
                                {d.filename}
                                <span className="text-slate-400 ml-1">({Math.round((d.size || 0) / 1024)} KB · {d.document_type})</span>
                              </span>
                              <button type="button"
                                onClick={() => removeUploadedDoc(d.document_id)}
                                className="text-red-600 hover:text-red-800 ml-2"
                                data-testid={`remove-doc-${d.document_id}`}>
                                <X className="w-3.5 h-3.5" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}

                {/* LCL / Air specific */}
                {(formData.service_type === "lcl" || formData.service_type === "air") && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-base font-medium">Cargo Items</Label>
                      <Button size="sm" variant="outline" onClick={addCargoItem}>
                        <Plus className="w-4 h-4 mr-1" />
                        Add Item
                      </Button>
                    </div>
                    
                    {formData.cargo_details.map((item, idx) => (
                      <div key={idx} className="p-4 bg-slate-50 rounded-lg space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm text-slate-600">Item {idx + 1}</span>
                          {formData.cargo_details.length > 1 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeCargoItem(idx)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-4 gap-3">
                          <div className="col-span-2">
                            <Label className="text-xs">Description</Label>
                            <Input
                              value={item.description}
                              onChange={(e) => updateCargoItem(idx, "description", e.target.value)}
                              placeholder="Item description"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Quantity</Label>
                            <Input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => updateCargoItem(idx, "quantity", e.target.value)}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Weight (kg)</Label>
                            <Input
                              type="number"
                              value={item.weight_kg}
                              onChange={(e) => updateCargoItem(idx, "weight_kg", e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <Label>Special Requirements</Label>
                  <Textarea
                    value={formData.special_requirements}
                    onChange={(e) => updateField("special_requirements", e.target.value)}
                    placeholder="Any special handling requirements, dangerous goods, temperature control, etc."
                    rows={3}
                    data-testid="special-requirements"
                  />
                </div>

                <div>
                  <Label>Service Contract Number (optional)</Label>
                  <Input
                    value={formData.service_contract_number}
                    onChange={(e) => updateField("service_contract_number", e.target.value)}
                    placeholder="Enter your service contract reference, if any"
                    data-testid="service-contract"
                  />
                </div>

                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    Back
                  </Button>
                  <Button onClick={() => setStep(4)}>
                    Continue
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Review & Submit */}
            {step === 4 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                <h2 className="text-xl font-semibold mb-4">Review & Submit</h2>

                {/* Summary */}
                <div className="bg-slate-50 rounded-xl p-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Service</p>
                      <p className="font-medium capitalize">{formData.service_type || "-"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Cargo Ready</p>
                      <p className="font-medium">{formData.cargo_ready_date || "-"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 py-3 border-t">
                    <div className="flex-1">
                      <p className="text-sm text-slate-500">Origin</p>
                      <p className="font-medium">{formData.origin_city}, {formData.origin_country}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                    <div className="flex-1 text-right">
                      <p className="text-sm text-slate-500">Destination</p>
                      <p className="font-medium">{formData.destination_city}, {formData.destination_country}</p>
                    </div>
                  </div>
                  <div className="border-t pt-3">
                    <p className="text-sm text-slate-500">Commodity</p>
                    <p className="font-medium">{formData.commodity || "-"}</p>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="space-y-4">
                  <Label className="text-base font-medium">Contact Information</Label>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm">Name</Label>
                      <Input
                        value={formData.contact_name}
                        onChange={(e) => updateField("contact_name", e.target.value)}
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Email</Label>
                      <Input
                        type="email"
                        value={formData.contact_email}
                        onChange={(e) => updateField("contact_email", e.target.value)}
                        data-testid="contact-email"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Phone</Label>
                      <Input
                        value={formData.contact_phone}
                        onChange={(e) => updateField("contact_phone", e.target.value)}
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm">Company Country</Label>
                      <Input
                        value={formData.company_country}
                        onChange={(e) => updateField("company_country", e.target.value)}
                        placeholder="e.g., SLOVENIA"
                        data-testid="company-country"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Additional Email Recipients (CC)</Label>
                      <Input
                        value={formData.cc_emails}
                        onChange={(e) => updateField("cc_emails", e.target.value)}
                        placeholder="comma-separated, e.g., ops@example.com, manager@example.com"
                        data-testid="cc-emails"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={() => setStep(3)}>
                    Back
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="submit-quote"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Submit Quote Request
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        )}

        {/* Quote History */}
        {activeTab === "history" && (
          <div>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : quotes.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-xl border">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-600">No quotes yet</h3>
                <p className="text-slate-400 mt-1">Submit a quote request to get started</p>
                <Button className="mt-4" onClick={() => setActiveTab("request")}>
                  <Plus className="w-4 h-4 mr-2" />
                  Request Quote
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {quotes.map((quote) => (
                  <motion.div
                    key={quote.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl border shadow-sm p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-blue-600">
                            {quote.quote_number || quote.id?.slice(0, 8)}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(quote.status)}`}>
                            {quote.status || "pending"}
                          </span>
                        </div>
                        <p className="text-slate-600 mt-1 text-sm">
                          {quote.commodity || quote.cargo_type || "Cargo"}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            <span>{quote.origin_city || quote.origin}</span>
                            <ChevronRight className="w-3 h-3" />
                            <span>{quote.destination_city || quote.destination}</span>
                          </div>
                          {quote.created_at && (
                            <span className="text-xs">
                              {new Date(quote.created_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        {quote.quoted_price && (
                          <div className="mt-3 p-2 bg-green-50 rounded-lg inline-flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-green-600" />
                            <span className="font-semibold text-green-700">
                              {quote.currency || "EUR"} {quote.quoted_price.toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedQuote(quote)}
                        data-testid={`view-quote-${quote.id}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Quote Detail Modal */}
        <AnimatePresence>
          {selectedQuote && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedQuote(null)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold">Quote Details</h2>
                    <p className="text-sm text-slate-500">
                      {selectedQuote.quote_number || selectedQuote.id?.slice(0, 8)}
                    </p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setSelectedQuote(null)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
                <div className="p-6 space-y-6">
                  <div className="flex items-center gap-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedQuote.status)}`}>
                      {selectedQuote.status || "pending"}
                    </span>
                    {selectedQuote.quoted_price && (
                      <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                        {selectedQuote.currency || "EUR"} {selectedQuote.quoted_price.toLocaleString()}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">Service Type</p>
                      <p className="font-medium capitalize">{selectedQuote.service_type || "-"}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">Commodity</p>
                      <p className="font-medium">{selectedQuote.commodity || "-"}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">Origin</p>
                      <p className="font-medium">
                        {selectedQuote.origin_city || selectedQuote.origin}, {selectedQuote.origin_country}
                      </p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-xs text-slate-500">Destination</p>
                      <p className="font-medium">
                        {selectedQuote.destination_city || selectedQuote.destination}, {selectedQuote.destination_country}
                      </p>
                    </div>
                  </div>

                  {selectedQuote.status === "quoted" && selectedQuote.quoted_price && (
                    <div className="flex gap-3 pt-4 border-t">
                      <Button
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        onClick={() => {
                          toast.success("Quote accepted! Our team will contact you shortly.");
                          setSelectedQuote(null);
                        }}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Accept Quote
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={() => setSelectedQuote(null)}
                      >
                        Review Later
                      </Button>
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
